import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { ReactComponent as AISearchSvg } from 'assets/svg/AI-Search-Icon.svg';
import { ReactComponent as LogoSearchSvg } from 'assets/svg/LogoSearch.svg';
import { ErrorMessage } from 'components';
import { debounce } from 'lodash';
import { useEffect, useRef, useCallback } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { aiSearchSchema } from 'schemas/aiSearchValidationSchema';
import { useFiltersStore } from 'store/useFiltersStore';
import usePropertiesStore from 'store/usePropertiesStore';
import { DEFAULT_DEBOUNCE_TIME, DEFAULT_FILTER } from 'util/Constants';

interface FormData {
    searchTerm: string;
}

const AiSearchButton = () => {
    return (
        <div className="relative flex">
            <div
                className="absolute top-0 left-0 rounded-[5px] w-full h-full z-0"
                style={{
                    backgroundColor: '#3176BC',
                }}
            ></div>
            <div
                className="flex items-center justify-center w-full gap-[5px] rounded-[5px] text-white text-[12px] m-[4px] z-[1] px-[20px] py-[5px]"
                style={{
                    backgroundColor: '#3176BC',
                }}
            >
                <span className="truncate py-[5px]">AI supported search</span>
            </div>
        </div>
    );
};

const AISearchPanel = () => {
    const { setCurrentFilter, currentFilter } = useFiltersStore();
    const { currentPage, fetchCompetitorAllPropertiesSort, loadingProperties } = usePropertiesStore();
    const queryClient = useQueryClient();
    const { control, handleSubmit, getValues, setValue, formState, reset, clearErrors } = useForm<FormData>({
        resolver: yupResolver(aiSearchSchema),
        defaultValues: { searchTerm: currentFilter.query || '' },
        mode: 'onSubmit', // Only validate on submit
    });

    // Create debounced refetch function
    const debouncedRefetch = useRef(
        debounce(() => {
            const newFilter = { ...currentFilter };
            delete newFilter.query;
            setCurrentFilter(newFilter);
            queryClient.refetchQueries({ queryKey: ['fetchCompetitorAllProperties', currentPage, newFilter, fetchCompetitorAllPropertiesSort] });
        }, DEFAULT_DEBOUNCE_TIME),
    );

    const handleFormReset = () => {
        const newFilter = { ...currentFilter, query: '' };
        setCurrentFilter(newFilter);
        setValue('searchTerm', '');
    };

    const onClickClear = () => {
        setValue('searchTerm', '');
        const newFilter = { ...currentFilter };
        delete newFilter.query;
        setCurrentFilter(newFilter);
        queryClient.refetchQueries({ queryKey: ['fetchCompetitorAllProperties', currentPage, newFilter, fetchCompetitorAllPropertiesSort] });
    };

    const handleInputChange = useCallback(
        (value: string, onChange: (value: string) => void) => {
            onChange(value);
            clearErrors('searchTerm'); // Clear errors on any input change

            // If the input is cleared, trigger debounced search after delay
            if (value.trim() === '' && currentFilter.query) {
                debouncedRefetch.current();
            }
        },
        [currentFilter.query, clearErrors],
    );

    const onSubmit: SubmitHandler<FormData> = ({ searchTerm }) => {
        if (!searchTerm.trim()) {
            return;
        }
        const newFilter = { ...currentFilter, query: searchTerm };
        setCurrentFilter(newFilter);
        queryClient.refetchQueries({ queryKey: ['fetchCompetitorAllProperties', currentPage, newFilter, fetchCompetitorAllPropertiesSort] });
    };

    useEffect(() => {
        const resetHandler = () => {
            reset();
            setValue('searchTerm', ''); // Explicitly clear the input value
            // Only clear the query field, preserve other filter values
            const newFilter = { ...currentFilter };
            delete newFilter.query;
            setCurrentFilter(newFilter);
        };
        window.addEventListener('resetAllForms', resetHandler);
        return () => {
            window.removeEventListener('resetAllForms', resetHandler);
        };
    }, [reset, currentFilter, setCurrentFilter, setValue]);

    return (
        <form className="flex flex-col gap-[12px] w-full" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-[8px] w-full">
                <p className="text-sm text-my-black lg:block hidden font-medium">
                    Below you can combine the filters with text-based searches — You will see 100% matches first, then closely related listings next. You can
                    also use the AI search with more details without the filters.
                </p>
                <div className="flex flex-col md:flex-row gap-[12px] w-full">
                    <div className="h-[48px] w-full">
                        <Controller
                            name="searchTerm"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <div className="h-[48px] w-full rounded-[8px] border border-my-gray border-opacity-80">
                                    <div className="flex justify-between items-center gap-[5px] h-full px-[10px]">
                                        <LogoSearchSvg />
                                        <input
                                            type="text"
                                            value={value}
                                            onChange={(e) => handleInputChange(e.target.value, onChange)}
                                            placeholder="Seaview, Terrace, Pool, Parking, etc."
                                            className="flex-grow"
                                            data-cy="ai-search-input"
                                            disabled={loadingProperties}
                                        />
                                        {getValues('searchTerm')?.length > 0 && (
                                            <div className="text-my-blue cursor-pointer" onClick={onClickClear} data-cy="ai-search-clear">
                                                Clear
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                    <button className="min-w-[200px] cursor-pointer" type="submit" disabled={loadingProperties}>
                        <AiSearchButton />
                    </button>
                </div>
            </div>
        </form>
    );
};

export default AISearchPanel;
